import {
  Button,
  Flex,
  FlexItem,
  Form,
  PageSection,
  PageSectionVariants,
  Tab,
  Tabs,
  TabTitleText,
  TextInput,
  Title,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import type { AppState } from '@app/state'
import * as ProgramScreenState from '@app/state/program_screen/state'
import { TabContent } from '@app/utils/styles/tab-content'

import { CustomProgramContent } from './program/custom-program-content'
import { ProgramInteractions } from './program/program-interactions'

const ProgramDetailsWrapper = styled.div`
  padding-bottom: 2em;
  .section-title {
    padding-bottom: 0.5em;
  }
`

interface UrlParams {
  id: string
}

enum TabKeys {
  Interactions,
  CustomContent,
}

export const ProgramScreen = () => {
  const dispatch = useDispatch()
  const { id } = useParams<UrlParams>()

  const [activeTabKey, setActiveTabKey] = useState<number>(TabKeys.Interactions)

  const setActiveTab = useCallback((_, selectedTab) => {
    setActiveTabKey(selectedTab)
  }, [])

  const { loadedProgram } = useSelector<AppState, ProgramScreenState.State>(
    (state) => state.programScreen,
  )

  const [programName, setProgramName] = useState<string>()

  useEffect(() => {
    dispatch(ProgramScreenState.actions.loadProgram(id))
  }, [dispatch, id])

  useEffect(() => {
    if (loadedProgram) {
      setProgramName(loadedProgram.name)
    }
  }, [loadedProgram])

  const handleUpdateProgramName = useCallback(() => {
    dispatch(
      ProgramScreenState.actions.updateProgram({
        ...loadedProgram,
        name: programName,
      }),
    )
  }, [dispatch, loadedProgram, programName])

  const handleCancelProgramName = useCallback(() => {
    setProgramName(loadedProgram?.name || '')
  }, [loadedProgram])

  const isFormUnchanged = programName === loadedProgram?.name

  return (
    <PageSection variant={PageSectionVariants.light}>
      <ProgramDetailsWrapper>
        <Title headingLevel="h1" size="xl" className="section-title">
          Configure Program
        </Title>
        <Form isHorizontal>
          <Flex>
            <FlexItem>
              <TextInput
                value={programName}
                isRequired
                type="text"
                id="program-name"
                name="programName"
                onChange={(value) => setProgramName(value)}
                placeholder="Program Name"
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                isDisabled={isFormUnchanged}
                onClick={handleCancelProgramName}
              >
                Reset
              </Button>
            </FlexItem>
            <FlexItem>
              <Button
                variant="primary"
                isDisabled={isFormUnchanged}
                onClick={handleUpdateProgramName}
              >
                Save
              </Button>
            </FlexItem>
          </Flex>
        </Form>
      </ProgramDetailsWrapper>

      <Tabs
        activeKey={activeTabKey}
        onSelect={setActiveTab}
        aria-label="Program Configuration Tabs"
        role="region"
      >
        <Tab
          eventKey={TabKeys.Interactions}
          title={<TabTitleText>Interactions</TabTitleText>}
        />
        <Tab
          eventKey={TabKeys.CustomContent}
          title={<TabTitleText>Custom Content</TabTitleText>}
        />
      </Tabs>

      <TabContent>
        {activeTabKey === TabKeys.Interactions ? (
          <>
            <ProgramInteractions
              key={loadedProgram?.programId}
              programId={loadedProgram?.programId}
            />
          </>
        ) : (
          <CustomProgramContent program={loadedProgram} />
        )}
      </TabContent>
    </PageSection>
  )
}
